import { AlertOctagon, RefreshCcw, Send } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional label — included in the error report POST. */
  context?: 'staff' | 'customer' | 'vet' | 'admin';
}

interface ErrorBoundaryState {
  error: Error | null;
  reported: 'idle' | 'sending' | 'sent' | 'failed';
}

/**
 * Top-level error boundary for the React tree.
 *
 * Catches render-time exceptions, shows a branded fallback, and lets the user
 * report the issue. POSTs to /api/error-reports which writes an activity_log
 * row — see apps/api/src/routes/error_reports.ts.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null, reported: 'idle' };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error, reported: 'idle' };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface to console for local debugging — in prod we POST below.
    console.error('ErrorBoundary caught:', error, info);
  }

  handleReport = async (): Promise<void> => {
    if (!this.state.error) return;
    this.setState({ reported: 'sending' });
    try {
      const res = await fetch('/api/error-reports', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: this.state.error.message,
          stack: this.state.error.stack ?? null,
          url: typeof window !== 'undefined' ? window.location.href : null,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
          context: this.props.context ?? 'staff',
        }),
      });
      this.setState({ reported: res.ok ? 'sent' : 'failed' });
    } catch {
      this.setState({ reported: 'failed' });
    }
  };

  handleReload = (): void => {
    if (typeof window !== 'undefined') window.location.reload();
  };

  override render(): ReactNode {
    if (!this.state.error) return this.props.children;

    return (
      <main className="grid min-h-[100dvh] place-items-center bg-background px-6 py-12 text-foreground">
        <div className="max-w-md text-center">
          <div className="mx-auto mb-6 grid size-14 place-items-center rounded-2xl bg-destructive/10 text-destructive">
            <AlertOctagon className="size-7" strokeWidth={1.5} />
          </div>
          <h1 className="font-heading text-2xl font-medium tracking-tight">Something went wrong</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            An unexpected error occurred. Reload to try again, or report this so we can fix it.
          </p>
          {this.state.error.message && (
            <pre className="mt-4 max-h-32 overflow-auto rounded-md border border-border bg-secondary/30 px-3 py-2 text-left text-xs text-muted-foreground">
              {this.state.error.message}
            </pre>
          )}
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button
              type="button"
              onClick={this.handleReload}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground press hover:bg-primary/90"
            >
              <RefreshCcw className="size-4" strokeWidth={1.75} />
              Reload
            </button>
            <button
              type="button"
              onClick={() => void this.handleReport()}
              disabled={this.state.reported === 'sending' || this.state.reported === 'sent'}
              className="inline-flex items-center gap-2 rounded-lg border border-border px-5 py-2.5 text-sm font-medium hover:bg-secondary press disabled:opacity-50"
            >
              <Send className="size-4" strokeWidth={1.75} />
              {this.state.reported === 'sending'
                ? 'Reporting…'
                : this.state.reported === 'sent'
                  ? 'Thanks — report sent'
                  : this.state.reported === 'failed'
                    ? 'Try report again'
                    : 'Report this'}
            </button>
          </div>
        </div>
      </main>
    );
  }
}
